import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api } from "@/api/client";
import type { AuthUser } from "@/auth/types";

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<AuthUser>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ME_KEY = ["auth", "me"] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  // /auth/me returns 401 when no session; we treat that as "logged out"
  // rather than an error condition. Any non-401 error bubbles up so the user
  // sees the network/server problem.
  const meQuery = useQuery<AuthUser | null>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<AuthUser>("/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 1;
    },
  });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ME_KEY });
  }, [qc]);

  const login = useCallback(
    async (email: string, password: string) => {
      const user = await api.post<AuthUser>("/auth/login", { email, password });
      qc.setQueryData(ME_KEY, user);
      return user;
    },
    [qc],
  );

  const logout = useCallback(async () => {
    try {
      await api.post<void>("/auth/logout");
    } finally {
      qc.setQueryData(ME_KEY, null);
      // Drop everything else too: any cached admin data belonged to the
      // previous user / session and is no longer trustworthy.
      await qc.invalidateQueries();
    }
  }, [qc]);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const user = await api.post<AuthUser>("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      qc.setQueryData(ME_KEY, user);
      return user;
    },
    [qc],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      refresh,
      login,
      logout,
      changePassword,
    }),
    [meQuery.data, meQuery.isLoading, refresh, login, logout, changePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
